import { useNavigation } from '@react-navigation/native';
import { BaseText, Button, ListItem, Meta, theme, Title } from 'aimo-ui';
import IconBadge from 'components/common/IconBadge';
import Icon from 'components/icons/Icon';
import { Row } from 'components/park/styles/styles';
import { format } from 'date-fns';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRetryPaymentStore } from 'stores/retryPaymentStore';
import styled from 'styled-components/native';
import { Pill } from 'styles/CommonLayoutStyles';
import { StyledImage, StyledWrapper } from 'styles/ContainerStyles';
import {
  BillingItem,
  Card,
  ChargingSession,
  CountryCode,
  Maybe,
  ParkingPaymentState,
  ParkingSession,
  Receipt,
} from 'types/generatedSchemaTypes';
import { AccountNavProps } from 'types/navigation';
import {
  currencyFormatter,
  getCorrectBillingItem,
  getCountryFromCurrency,
  getLocale,
} from 'utils/commonUtils';
import { HistoryScreenName, HistoryType } from 'utils/historyUtils';
import { locationIcon } from 'utils/parkUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { getDurationFromDates } from 'utils/timeUtils';

const calculateNetAmountCents = (
  totalAmountCents: Maybe<number> | undefined,
  refundHistory: Receipt['refundHistory']
): number => {
  const refundTotal = (refundHistory || []).reduce(
    (sum, r) => sum + (r?.refundedAmountCents || 0),
    0
  );
  return (totalAmountCents ?? 0) - refundTotal;
};

const resolveCountryCode = (
  countryCode: Maybe<string> | undefined,
  currency: Maybe<string> | undefined
): CountryCode =>
  (countryCode ||
    getCountryFromCurrency(currency as string) ||
    'SE') as CountryCode;

const StyledView = styled.View`
  margin-right: 4px;
`;

const PayNowButtonWrapper = styled.View`
  padding-horizontal: 16px;
  padding-bottom: 12px;
  padding-top: 4px;
`;

type PayNowButtonProps = {
  onPress?: () => void;
  testID: string;
  text: string;
  disabled?: boolean;
};

const PayNowButton: React.FC<PayNowButtonProps> = ({
  onPress,
  testID,
  text,
  disabled = false,
}) => (
  <PayNowButtonWrapper>
    <Button
      size="m"
      text={text}
      onPress={onPress}
      testID={testID}
      disabled={disabled}
    />
  </PayNowButtonWrapper>
);

type ItemHistoryDisplayProps = {
  groupIndex: number;
  index: number;
};

type ItemReceiptDisplayProps = {
  item: Receipt;
} & ItemHistoryDisplayProps;

type ItemParkingDisplayProps = {
  item: ParkingSession;
} & ItemHistoryDisplayProps;

type ItemChargingDisplayProps = {
  item: ChargingSession;
} & ItemHistoryDisplayProps;

type PaymentRowProps = {
  testID: string;
  secondLine: string;
  isPaymentFailed: boolean;
  firstLine: string;
  iconName?: string;
  date?: Date;
};

interface NoHistoryProps {
  isFailed?: boolean;
  isFree?: boolean;
  isBilling?: boolean;
  isOther?: boolean;
  historyScreenName?: HistoryScreenName;
}

export const ticketName = (
  zoneName: Maybe<string> | undefined,
  zoneCode?: Maybe<string>,
  defaultText?: string
) => {
  if (!zoneName) {
    return defaultText ?? ' ';
  }
  const zoneCodePrefix = zoneCode ? `${zoneCode} ` : '';
  return `${zoneCodePrefix}${zoneName}`;
};

const ArrowRight = () => <Icon name="Chevron_Right" size="s" />;

const ChargingIcon = () => (
  <IconBadge name="Charging" backgroundColor={theme.colors.lightBg.green} />
);

const PaymentRow = ({
  testID,
  secondLine,
  isPaymentFailed,
  firstLine,
  iconName,
  date,
}: PaymentRowProps) => {
  const { t } = useTranslation();
  return (
    <Row mt={0} mb={0} testID={`${testID}-payment-details`}>
      {iconName && (
        <StyledView>
          <Icon name={locationIcon[iconName]} size={'s'} />
        </StyledView>
      )}
      {date && (
        <Meta color={theme.colors.gray300}>{`${format(date, 'dd.MM', {
          locale: getLocale(),
        })}.  •  `}</Meta>
      )}
      <Meta color={theme.colors.gray300}>
        {firstLine ? `${firstLine}  •  ` : ''}
      </Meta>
      <Meta
        color={
          !isPaymentFailed ? theme.colors.gray300 : theme.colors.boost.red
        }>
        {!isPaymentFailed ? secondLine : t('parking.receipt.paymentFailed')}
      </Meta>
    </Row>
  );
};

export const ItemPermitHistoryDisplay: React.FC<ItemReceiptDisplayProps> = ({
  item,
  groupIndex,
  index,
}) => {
  const {
    created,
    countryCode,
    items,
    totalAmountCents,
    currency,
    orderNumber,
    transactionId,
  } = item;
  const { t } = useTranslation();
  const { navigate } = useNavigation<AccountNavProps>();
  const id = items?.[0]?.permitId;
  const type = items?.[0]?.type;
  const defaultText =
    type === 'AIMO_PLUS'
      ? t('account.aimoSubscription.title')
      : t('parking.receipt.permitDefaultName');
  const name = ticketName(
    items?.[0]?.zoneName,
    items?.[0]?.zoneCode,
    defaultText
  );
  const isClickable = !!id || !!orderNumber;
  const navigateToPermitDetails = () => {
    if (id || transactionId) {
      navigate('permitHistoryDetails', {
        permitId: id ?? '',
        transactionId,
      });
    }
  };

  const countryCodeCheck = resolveCountryCode(countryCode, currency);
  const price = totalAmountCents ?? 0;
  const pricing =
    totalAmountCents !== 0
      ? currencyFormatter(price / 100, countryCodeCheck)
      : t('price.free');

  const formattedDate = created
    ? format(new Date(created), 'dd.MM.', { locale: getLocale() })
    : '';
  const accessibilityLabel = `${t('longTermParking.30DayPermits')}: ${name}, ${formattedDate} ${pricing}`;

  return (
    <ListItem
      item={item}
      title={name}
      onPress={navigateToPermitDetails}
      testID={`permit-item-${groupIndex}-${index}`}
      accessible={true}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      iconComponent={IconBadge}
      iconName={type === 'AIMO_PLUS' ? 'Aimo-Plus' : 'Parking'}
      indexValue={index}
      subtitleComponent={() => (
        <PaymentRow
          testID={`permit-item-${groupIndex}-${index}`}
          firstLine={
            type === 'AIMO_PLUS' ? '' : t('longTermParking.30DayPermits')
          }
          secondLine={pricing}
          isPaymentFailed={false}
          date={created ? new Date(created) : undefined}
        />
      )}
      arrowComponent={() =>
        isClickable ? <Icon name="Chevron_Right" size="s" /> : null
      }
    />
  );
};

const RightContent = ({
  paymentCard,
  paymentState,
  groupIndex,
  index,
  isFailedBilling,
}: {
  paymentCard: Card;
  paymentState?: ParkingPaymentState;
  groupIndex: number;
  index: number;
  isFailedBilling?: boolean;
}) => {
  const { t } = useTranslation();
  const category = paymentCard?.category;
  const padgeText =
    paymentState && paymentState === 'Billing'
      ? t('paymentMethods.billing')
      : t('paymentMethods.business');
  const bgColor = isFailedBilling ? theme.colors.boost.yellow : undefined;
  if ((paymentState && paymentState === 'Billing') || category === 'business') {
    return (
      <Pill bg={bgColor}>
        <Meta testID={`parking-item-business-chip-${groupIndex}-${index}`}>
          {padgeText}
        </Meta>
      </Pill>
    );
  }
  return <></>;
};

export const ParkingSessionHistoryItem: React.FC<ItemParkingDisplayProps> = ({
  item,
  groupIndex,
  index,
}) => {
  const {
    price,
    countryCode,
    startDate,
    endDate,
    parkingZoneUid,
    sessionId,
    paymentState,
    cardId,
    parkZone,
    paymentType,
  } = item || {};
  const { t } = useTranslation();
  const paymentCard = useSelectedPaymentMethod(cardId ?? '');
  const paymentStateFromPolling = useRetryPaymentStore((s) =>
    s.getSessionStatus(sessionId as string, paymentState)
  );
  const isRetryQueued =
    paymentStateFromPolling === 'Retrying' ||
    paymentStateFromPolling === 'Pending';

  const { navigate } = useNavigation<AccountNavProps>();
  const { name: parkingZoneName, commercialZoneView } = parkZone || {};

  const navigateToDetails = () => {
    navigate('parkingHistoryDetails', {
      sessionId,
      parkingZoneUid: parkingZoneUid as string,
    });
  };

  const duration =
    startDate && endDate
      ? getDurationFromDates(new Date(startDate), new Date(endDate))
      : '';

  const totalAmountWithCurrency =
    price?.totalAmount !== 0
      ? currencyFormatter(price?.totalAmount || 0, countryCode)
      : t('price.free');

  const statusLabel = (() => {
    if (isRetryQueued) return t('button.paymentQueued');
    if (paymentStateFromPolling === 'Success')
      return t('button.paymentSuccess');
    return t('parking.receipt.payNow');
  })();

  const isFailedBilling =
    paymentState === 'Billing' && paymentType !== 'CORPORATE_ACCOUNT';

  const showPayNowButton =
    paymentStateFromPolling !== 'Success' &&
    paymentType !== 'CORPORATE_ACCOUNT';
  return (
    <ListItem
      item={item}
      title={(commercialZoneView?.name || parkingZoneName) as string}
      onPress={navigateToDetails}
      testID={`parking-item-${groupIndex}-${index}`}
      iconComponent={IconBadge}
      iconName="Parking"
      indexValue={index}
      subtitleComponent={() => (
        <PaymentRow
          testID={`parking-item-${groupIndex}-${index}`}
          firstLine={duration}
          secondLine={totalAmountWithCurrency}
          isPaymentFailed={paymentState === 'Failed'}
          date={startDate ? new Date(startDate) : undefined}
        />
      )}
      rightContentComponent={() => (
        <RightContent
          isFailedBilling={isFailedBilling}
          paymentCard={paymentCard || {}}
          paymentState={paymentState || undefined}
          groupIndex={groupIndex}
          index={index}
        />
      )}
      rightContentAlignCenter
      arrowComponent={ArrowRight}
      bottomComponent={
        showPayNowButton ? (
          <PayNowButton
            text={statusLabel}
            onPress={navigateToDetails}
            testID={`parking-item-pay-now-${groupIndex}-${index}`}
            disabled={isRetryQueued}
          />
        ) : undefined
      }
    />
  );
};

export const ChargingSessionHistoryItem: React.FC<ItemChargingDisplayProps> = ({
  item,
  groupIndex,
  index,
}) => {
  const {
    startTime,
    endTime,
    parkingZoneUid,
    chargingSessionId,
    cardId,
    created,
    paymentState,
    parkZone,
    priceInCents,
    countryCode,
  } = item || {};
  const { t } = useTranslation();
  const { navigate } = useNavigation<AccountNavProps>();
  const { commercialZoneView } = parkZone || {};
  const paymentCard = useSelectedPaymentMethod(cardId ?? '');
  const paymentStateFromPolling = useRetryPaymentStore((s) =>
    s.getChargingSessionStatus(chargingSessionId as string, paymentState)
  );
  const isRetryQueued =
    paymentStateFromPolling === 'Retrying' ||
    paymentStateFromPolling === 'Pending';

  const navigateToCharigingHistoryDetails = () => {
    navigate('chargingHistoryDetails', {
      chargingSessionId: chargingSessionId as string,
      parkingZoneUid: parkingZoneUid as string,
    });
  };
  const country = countryCode || 'SE';

  const duration =
    startTime && endTime
      ? getDurationFromDates(new Date(startTime), new Date(endTime))
      : '';

  const isPaymentFailed =
    paymentStateFromPolling === 'Failed' ||
    paymentStateFromPolling === 'Retrying';
  const totalAmountWithCurrency =
    priceInCents !== 0
      ? currencyFormatter((priceInCents || 0) / 100, country)
      : t('price.free');
  const badgeDate = startTime || created;

  const statusLabel = (() => {
    if (isRetryQueued) return t('button.paymentQueued');
    if (paymentStateFromPolling === 'Success')
      return t('button.paymentSuccess');
    return t('parking.receipt.payNow');
  })();

  const showPayNowButton = paymentStateFromPolling !== 'Success';

  return (
    <ListItem
      item={item}
      title={
        commercialZoneView?.name
          ? commercialZoneView?.name
          : t('parking.receipt.chargingDefaultName')
      }
      onPress={navigateToCharigingHistoryDetails}
      testID={`charging-item-${groupIndex}-${index}`}
      iconComponent={ChargingIcon}
      iconName="Charging"
      indexValue={index}
      accessible={true}
      accessibilityLabel={`${t('services.charging')}: ${t('parking.receipt.chargingDefaultName')}, ${badgeDate ? format(new Date(badgeDate), 'dd.MM.', { locale: getLocale() }) : ''} ${duration} ${totalAmountWithCurrency}`}
      accessibilityRole="button"
      subtitleComponent={() => (
        <PaymentRow
          testID={`charging-item-${groupIndex}-${index}`}
          firstLine={duration}
          secondLine={totalAmountWithCurrency}
          isPaymentFailed={isPaymentFailed}
          date={badgeDate ? new Date(badgeDate) : undefined}
        />
      )}
      rightContentComponent={() => (
        <RightContent
          paymentCard={paymentCard || {}}
          paymentState={paymentState || undefined}
          groupIndex={groupIndex}
          index={index}
        />
      )}
      rightContentAlignCenter
      arrowComponent={ArrowRight}
      bottomComponent={
        isPaymentFailed && showPayNowButton ? (
          <PayNowButton
            text={statusLabel}
            onPress={navigateToCharigingHistoryDetails}
            testID={`charging-item-pay-now-${groupIndex}-${index}`}
            disabled={isRetryQueued}
          />
        ) : undefined
      }
    />
  );
};

export const ItemParkingHistoryDisplay: React.FC<ItemReceiptDisplayProps> = ({
  item,
  groupIndex,
  index,
}) => {
  const {
    items,
    totalAmountCents,
    countryCode,
    creditCard,
    currency,
    transactionId,
    refundHistory,
  } = item || {};
  const { t } = useTranslation();
  // const { flags: { retry_payment } } = useContext(FeatureFlagsContext); Not use for now. Retry feature implement on different ticket
  // const showRetry = showFeatureFlag(retry_payment); Not use for now. Retry feature implement on different ticket
  const paymentCard = useSelectedPaymentMethod(creditCard?.cardId ?? '');

  const countryCodeCheck = resolveCountryCode(countryCode, currency);
  const correctItems = getCorrectBillingItem(
    items as BillingItem[] | undefined
  );

  const startDate = correctItems?.[0]?.startTime;
  const endDate = correctItems?.[0]?.endTime;
  const parkingZoneUid = correctItems?.[0]?.zoneUid;
  const sessionId = correctItems?.[0]?.parkingSessionId;
  const type = correctItems?.[0]?.type;
  const zoneCode = correctItems?.[0]?.zoneCode;
  const name = correctItems?.[0]?.zoneName;

  const title = ticketName(
    name,
    zoneCode,
    type === 'PREPAID'
      ? t('parking.receipt.prepaidDefaultName')
      : t('parking.receipt.parkingDefaultName')
  );

  const { navigate } = useNavigation<AccountNavProps>();

  const isClickable =
    type === 'PREPAID'
      ? transactionId || sessionId
      : transactionId && parkingZoneUid;

  const navigateToHistoryDetails = () => {
    if (type === 'PREPAID') {
      navigate('prepaidHistoryDetails', {
        ticketId: sessionId || '',
        transactionId,
      });
    } else {
      navigate('parkingHistoryDetails', {
        transactionId,
        sessionId: sessionId || '',
        parkingZoneUid: parkingZoneUid as string,
      });
    }
  };

  const duration =
    startDate && endDate
      ? getDurationFromDates(new Date(startDate), new Date(endDate))
      : '';

  const price = calculateNetAmountCents(totalAmountCents, refundHistory);
  const totalAmountWithCurrency =
    totalAmountCents !== 0
      ? currencyFormatter(price / 100, countryCodeCheck)
      : t('price.free');

  const isPaymentFailed = false; //For now not handling payment fail

  const priceString = !isPaymentFailed
    ? totalAmountWithCurrency
    : t('parking.receipt.paymentFailed');

  // Create a comprehensive accessibility label that includes all important information
  const formattedDate = startDate
    ? format(new Date(startDate), 'dd.MM.', { locale: getLocale() })
    : '';
  const accessibilityLabel = `${title}, ${formattedDate} ${duration} ${priceString}`;

  return (
    <ListItem
      item={item}
      title={title}
      onPress={isClickable ? navigateToHistoryDetails : undefined}
      testID={`parking-item-${groupIndex}-${index}`}
      accessible={true}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      iconComponent={useCallback(
        () => (
          <IconBadge
            backgroundColor={
              type === 'PREPAID' ? theme.colors.lightBg.green : undefined
            }
            name="Parking"
          />
        ),
        [type]
      )}
      indexValue={index}
      subtitleComponent={() => (
        <PaymentRow
          testID={`parking-item-${groupIndex}-${index}`}
          firstLine={type === 'PREPAID' ? t('services.prepaidTitle') : duration}
          secondLine={priceString}
          isPaymentFailed={isPaymentFailed}
          date={startDate ? new Date(startDate) : undefined}
        />
      )}
      rightContentComponent={() =>
        paymentCard?.category === 'business' && (
          <Pill>
            <Meta testID={`parking-item-business-chip-${groupIndex}-${index}`}>
              {t('paymentMethods.business')}
            </Meta>
          </Pill>
        )
      }
      rightContentAlignCenter
      arrowComponent={() =>
        isClickable ? (
          <Icon
            name="Chevron_Right"
            {...testIdentifiers(`parking-item-${groupIndex}-${index}-chevron`)}
            size="s"
          />
        ) : null
      }
    />
  );
};

export const ItemChargingHistoryDisplay: React.FC<ItemReceiptDisplayProps> = ({
  item,
  groupIndex,
  index,
}) => {
  const {
    items,
    totalAmountCents,
    countryCode,
    creditCard,
    currency,
    transactionId,
    refundHistory,
  } = item || {};
  const { t } = useTranslation();

  const countryCodeCheck = resolveCountryCode(countryCode, currency);
  const correctItems = getCorrectBillingItem(
    items as BillingItem[] | undefined
  );

  const startTime = correctItems?.[0]?.startTime;
  const endTime = correctItems?.[0]?.endTime;
  const parkingZoneUid = correctItems?.[0]?.zoneUid;
  const name = ticketName(
    correctItems?.[0]?.zoneName,
    correctItems?.[0]?.zoneCode,
    t('parking.receipt.chargingDefaultName')
  );

  const isClickable = transactionId;

  const { navigate } = useNavigation<AccountNavProps>();
  const paymentCard = useSelectedPaymentMethod(creditCard?.cardId ?? '');

  const navigateToChargingHistory = () => {
    if (isClickable) {
      navigate('chargingHistoryDetails', {
        transactionId,
        parkingZoneUid: parkingZoneUid as string,
      });
    }
  };

  const duration =
    startTime && endTime
      ? getDurationFromDates(new Date(startTime), new Date(endTime))
      : '';

  const price = calculateNetAmountCents(totalAmountCents, refundHistory);
  const pricing =
    totalAmountCents !== 0
      ? currencyFormatter(price / 100, countryCodeCheck)
      : t('price.free');

  const isPaymentFailed = false; // Not handling payment fail right now

  const priceString = !isPaymentFailed
    ? pricing
    : t('parking.receipt.paymentFailed');

  // Create a comprehensive accessibility label that includes all important information
  const formattedDate = startTime
    ? format(new Date(startTime), 'dd.MM.', { locale: getLocale() })
    : '';
  const accessibilityLabel = `${t('services.charging')}: ${name}, ${formattedDate} ${duration} ${priceString}`;

  return (
    <ListItem
      item={item}
      title={name}
      onPress={navigateToChargingHistory}
      testID={`charging-item-${groupIndex}-${index}`}
      iconComponent={ChargingIcon}
      iconName="Charging"
      indexValue={index}
      accessible={true}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      subtitleComponent={() => (
        <PaymentRow
          testID={`charging-item-${groupIndex}-${index}`}
          firstLine={duration}
          secondLine={priceString}
          isPaymentFailed={isPaymentFailed}
          date={startTime ? new Date(startTime) : undefined}
        />
      )}
      rightContentComponent={() =>
        paymentCard?.category === 'business' && (
          <Pill>
            <Meta testID={`parking-item-business-chip-${groupIndex}-${index}`}>
              {t('paymentMethods.business')}
            </Meta>
          </Pill>
        )
      }
      rightContentAlignCenter
      arrowComponent={() =>
        isClickable ? <Icon name="Chevron_Right" size="s" /> : null
      }
    />
  );
};

export const NoHistory: React.FC<NoHistoryProps> = ({
  isFailed,
  isFree,
  isBilling,
  isOther,
  historyScreenName,
}) => {
  const { t } = useTranslation();

  const getHistoryType = (): HistoryType => {
    if (isFailed) {
      return 'failed';
    }
    if (isFree) {
      return 'free';
    }
    if (isBilling) {
      return 'billing';
    }
    if (isOther) {
      return 'other';
    }
    return 'default';
  };

  const getTitleKey = (type: HistoryType): string => {
    const titleMap: Record<HistoryType, string> = {
      failed: 'parking.noFailedPayments',
      free: 'parking.noFreeHistory',
      billing:
        historyScreenName === 'PREPAID_AND_PERMIT'
          ? 'parkingHistoryFilter.noBillingHistoryForPrepaidTicket'
          : 'parking.noBillingHistory',
      other: 'parking.noOtherHistory',
      default: 'parking.noHistory',
    };
    return titleMap[type];
  };

  const getDescriptionKey = (type: HistoryType): string => {
    if (type === 'failed') {
      return 'parking.noHistoryDescFailed';
    }
    return historyScreenName === 'PREPAID_AND_PERMIT'
      ? 'parkingHistoryFilter.noBillingHistoryForPrepaidTicketDescription'
      : 'parking.noHistoryDescription';
  };

  const historyType = getHistoryType();
  const titleKey = getTitleKey(historyType);
  const descriptionKey = getDescriptionKey(historyType);

  return (
    <>
      <StyledWrapper center mb={0}>
        <StyledImage
          testID="parking-history-no-content-image"
          width={183}
          height={152}
          center
          source={require('assets/images/parking-receipt-car.png')}
        />
      </StyledWrapper>
      <StyledWrapper center mb={37}>
        <Title center testID="parking-history-no-content-title">
          {t(titleKey)}
        </Title>
      </StyledWrapper>
      <StyledWrapper center maxWidth={300}>
        <BaseText center testID="parking-history-no-content-description">
          {t(descriptionKey)}
        </BaseText>
      </StyledWrapper>
    </>
  );
};
